<template>
  <PageHeader>
    <template #header-title>
      <WdsStack direction-column gap-md>
        <WdsStack gap-md align-center justify-center>
          <WdsAvatar :path="avatarPath" lg />
          {{ $t('Hi, :user', { user: userName }) }}
        </WdsStack>
        <WdsStack gap-sm>
          <WdsLabel primary subtle>
            <WdsStack justify-center align-center gap-xs>
              <WdsIcon :name="accountIcon" :size="12" />
              {{ accountName }}
            </WdsStack>
          </WdsLabel>
          <WdsLabel subtle>
            <WdsStack justify-center align-center gap-xs>
              <WdsIcon name="user" :size="12" />
              {{ $t(':role role', { role: roleName }) }}
            </WdsStack>
          </WdsLabel>
        </WdsStack>
      </WdsStack>
    </template>
    <template #header-actions>
      <WdsStack class="tw-mb-2" direction-row gap-md align-center>
        <WdsStack
          v-if="hasNewDashboard"
          class="new-experience"
          gap-sm
          align-center
          justify-center
        >
          <WdsToggle v-model:checked="newDashboard" primary data-test="new-experience" />
          <WdsText>{{ $t('New experience') }}</WdsText>
          <WdsLabel sm warning subtle>
            Beta
            <WdsIcon
              :size="13"
              weight="thin"
              :name="WdsIconNameEnum.LIGHT"
              class="!tw-inline-block"
            />
          </WdsLabel>
        </WdsStack>
      </WdsStack>
    </template>
    <template #header-description>
      <slot name="header-alerts"></slot>
    </template>
  </PageHeader>

  <!-- Default/old dashboard -->
  <slot v-if="!shouldShowNewDashboard"></slot>

  <!-- Company Dashboard -->
  <CompanyDashboard v-else-if="showCompanyDashboard" />

  <!-- Worker Dashboard -->
  <!-- <FreelancerDashboard v-else-if="showFreelancerDashboard" /> -->

</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsToggle,
  WdsLabel,
  WdsIcon,
  WdsIconNameEnum,
  WdsText,
  WdsAvatar,
} from '@worksome/wds-components'
import { computed } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import PageHeader from '@/components/PageHeader.vue'
import { getAvatarPath } from '@/utils/avatar'
import AccountType from '@/utils/AccountType'
import CompanyDashboard from '@/components/Dashboard/Types/CompanyDashboard.vue'
// import FreelancerDashboard from '@/components/Dashboard/Types/FreelancerDashboard.vue'

const hasNewDashboard = computed(() =>
   AccountType.isCompany(currentAccountType)
  // || AccountType.isFreelancer(currentAccountType)
)

const newDashboard = useLocalStorage('dashboardVersion', false)

const shouldShowNewDashboard = computed(() => hasNewDashboard.value && newDashboard.value)

const showCompanyDashboard = computed(() => AccountType.isCompany(currentAccountType))
// const showFreelancerDashboard = computed(() => AccountType.isFreelancer(currentAccountType))

const avatarPath = computed(() => {
  const pic: string | undefined = currentUser.picture
  return getAvatarPath(pic, currentUser.globalId)
})

const userName = computed(() => currentUser.name)
const roleName = computed(() => currentTeamRoleName)
const accountName = computed(() => currentAccount.name)
const accountIcon = computed((): WdsIconNameEnum => {
  if (AccountType.isWorker(currentAccountType)) {
    return WdsIconNameEnum.USER_PROFILE_ALT
  }
  return WdsIconNameEnum.BUILDING
})
</script>

<style lang="scss" scoped>
:deep(.wds-toggle-wrapper .toggle-cursor-pointer input) {
  display: contents;
}

@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  70% {
    background-position: 50% 50%;
  }
  85% {
    background-position: 150% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

.new-experience {
  position: relative;
  padding: 8px;
  border-radius: 40px;
  background: linear-gradient(90deg, #c3b6f9, #ffc89e, #c3b6f9);
  background-size: 200% 200%;
  animation: gradient-animation 5s ease-in-out 4;
  animation-fill-mode: forwards;
}
.new-experience::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  border-radius: 38px;
  background: white;
  z-index: 1;
}
.new-experience > * {
  position: relative;
  z-index: 2;
}

/* Card styling */
.dashboard-card {
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 1.25rem;
    margin-bottom: 0.75rem;
  }

  .card-metric {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }

  .card-footer {
    margin-top: 0.75rem;
    color: #888888;
    font-size: 0.875rem;
  }
}
</style>
